import React from 'react';

interface DashboardCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  isClickable?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  onClick,
  className = '',
  isClickable = false,
  variant = 'default',
}) => {
  const variantClasses = {
    default: {
      card: 'bg-white border-gray-200',
      title: 'text-gray-600',
      value: 'text-gray-900',
      subtitle: 'text-gray-500',
      icon: 'text-gray-400',
      hover: 'hover:shadow-lg hover:border-gray-300',
    },
    success: {
      card: 'bg-green-50 border-green-300 shadow-green-100',
      title: 'text-green-900',
      value: 'text-green-700',
      subtitle: 'text-green-700',
      icon: 'text-green-500',
      hover: 'hover:shadow-lg hover:border-green-400 hover:bg-green-100',
    },
    warning: {
      card: 'bg-yellow-50 border-yellow-300 shadow-yellow-100',
      title: 'text-yellow-900',
      value: 'text-yellow-700',
      subtitle: 'text-yellow-700',
      icon: 'text-yellow-500',
      hover: 'hover:shadow-lg hover:border-yellow-400 hover:bg-yellow-100',
    },
    danger: {
      card: 'bg-red-50 border-red-300 shadow-red-100',
      title: 'text-red-900',
      value: 'text-red-700',
      subtitle: 'text-red-700',
      icon: 'text-red-500',
      hover: 'hover:shadow-lg hover:border-red-400 hover:bg-red-100',
    },
  }[variant];

  const baseClasses = `rounded-lg shadow-md p-6 border ${variantClasses.card}`;
  const hoverClasses = isClickable ? `${variantClasses.hover} cursor-pointer transition-all duration-200` : '';

  return (
    <div
      className={`${baseClasses} ${hoverClasses} ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className={`text-sm font-medium mb-2 ${variantClasses.title}`}>{title}</h3>
          <div className="flex items-center">
            <span className={`text-3xl font-bold ${variantClasses.value}`}>{value}</span>
          </div>
          {subtitle && (
            <p className={`text-sm mt-1 ${variantClasses.subtitle}`}>{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className={variantClasses.icon}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardCard;
